import { LinearMarketingTimeline } from "../timeline";
import { withPinnedProgress } from "../blocks/pin";
import { sectionSpanInScreens } from "../score";
import { NarrativeDirector } from "../narrative";
import {
  SECTION_IDS,
  type ExperienceSnapshot,
  type MarketingFrame,
  type MarketingLayout,
  type MarketingRenderInput,
  type MarketingViewport,
  type PresentationSnapshot,
  type ProductProofsSnapshot,
  type SectionId,
  type SubsystemSnapshot,
} from "../types";

export interface MarketingPresentationPort {
  measure(): MarketingLayout;
  render(frame: MarketingFrame): PresentationSnapshot;
  destroy(): void;
}

export class MarketingPresentation implements MarketingPresentationPort {
  private readonly sections = {} as Record<SectionId, HTMLElement>;
  private lastTotalSpanPx = -1;

  constructor(private readonly root: HTMLElement) {
    for (const id of SECTION_IDS) {
      const section = root.querySelector<HTMLElement>(`[data-section-id="${id}"]`);
      if (!section) throw new Error(`Missing marketing section: ${id}`);
      this.sections[id] = section;
    }
    root.dataset.marketingEnhanced = "true";
  }

  measure(): MarketingLayout {
    const viewportPx = Math.max(1, this.root.ownerDocument.defaultView?.innerHeight ?? 1);
    /* Длина каждого блока приходит из партитуры, а не из CSS. Раньше её задавали
       три файла стилей подряд, и последний по порядку подключения затирал
       остальные: двенадцать разных длительностей превращались в девять
       одинаковых. Теперь источник один. */
    /* На телефоне партитура длительностей не действует: там раздел — это
       ровно один экран, показанный в досмотренном виде и переключаемый
       импульсом или стрелкой. Удерживать нечего, значит и длина у всех одна. */
    const mobile = (this.root.ownerDocument.defaultView?.innerWidth ?? 1024) < 700;
    let totalSpanPx = 0;
    for (const id of SECTION_IDS) {
      const node = this.sections[id];
      if (!node) continue;
      const spanPx = mobile ? viewportPx : Math.round(sectionSpanInScreens(id) * viewportPx);
      totalSpanPx += spanPx;
      node.style.minHeight = `${spanPx}px`;
      node.style.setProperty("--section-span", `${spanPx}px`);
    }
    /* Длина прокрутки живёт снаружи маршрута — её считает фильм по высоте
       маршрута. Раз высоту теперь задаёт партитура, а не CSS, фильм узнаёт о
       ней только здесь. Молчание стоило шести последних блоков: прокрутка
       заканчивалась на 10.9 экрана раньше маршрута. */
    if (totalSpanPx !== this.lastTotalSpanPx) {
      this.lastTotalSpanPx = totalSpanPx;
      this.root.ownerDocument.defaultView?.dispatchEvent(new Event("bb:route-measured"));
    }

    const nodes = Array.from(this.root.querySelectorAll<HTMLElement>("[data-section-id]"));
    const sections = {} as MarketingLayout["sections"];
    SECTION_IDS.forEach((id, index) => {
      const node = nodes[index];
      if (!node || node.dataset.sectionId !== id) {
        throw new Error(`Marketing route must expose SectionId ${id} at position ${index}.`);
      }
      sections[id] = {
        startPx: node.offsetTop,
        endPx: node.offsetTop + Math.max(1, node.offsetHeight),
      };
    });
    if (nodes.length !== SECTION_IDS.length) {
      throw new Error(`Marketing route must contain exactly ${SECTION_IDS.length} sections.`);
    }
    const viewportHeight = Math.max(1, this.root.ownerDocument.defaultView?.innerHeight ?? 1);
    const routeHeight = Math.max(this.root.scrollHeight, sections.footer.endPx);
    return {
      viewportHeight,
      routeHeight,
      maxTravel: Math.max(1, routeHeight - viewportHeight),
      sections,
    };
  }

  render(frame: MarketingFrame): PresentationSnapshot {
    const visible = frame.handoff > 0.02;
    this.root.style.opacity = frame.handoff.toFixed(3);
    this.root.style.pointerEvents = frame.handoff > 0.35 ? "auto" : "none";
    this.root.style.transform = `translate3d(0, ${(-frame.travelPx).toFixed(2)}px, 0)`;
    this.root.setAttribute("aria-hidden", String(!visible));
    this.root.toggleAttribute("inert", !visible);
    this.root.dataset.activeSection = frame.activeSection;
    this.root.dataset.marketingDirection = String(frame.direction);
    for (const id of SECTION_IDS) {
      const active = id === frame.activeSection;
      this.sections[id].style.setProperty("--section-progress", frame.sections[id].progress.toFixed(4));
      this.sections[id].toggleAttribute("data-active", active);
      this.sections[id].toggleAttribute("inert", !active);
      this.sections[id].setAttribute("aria-hidden", String(!active));
    }
    return { activeSection: frame.activeSection, visible, travelPx: frame.travelPx };
  }

  destroy(): void {
    this.root.removeAttribute("style");
    this.root.removeAttribute("aria-hidden");
    this.root.removeAttribute("inert");
    delete this.root.dataset.activeSection;
    delete this.root.dataset.marketingDirection;
    delete this.root.dataset.marketingEnhanced;
    for (const id of SECTION_IDS) {
      this.sections[id].style.removeProperty("--section-progress");
      this.sections[id].removeAttribute("data-active");
      this.sections[id].removeAttribute("aria-hidden");
      this.sections[id].removeAttribute("inert");
    }
  }
}

export interface MarketingFramePort {
  render(frame: MarketingFrame, now: number): SubsystemSnapshot;
  resize?(viewport: MarketingViewport, layout: MarketingLayout): void;
  destroy?(): void;
}

export interface MarketingOfferPort {
  snapshot(): SubsystemSnapshot;
  destroy?(): void;
}

export interface MarketingExperienceOptions {
  timeline: LinearMarketingTimeline;
  presentation: MarketingPresentationPort;
  world: MarketingFramePort;
  navigation: MarketingFramePort;
  editorial: MarketingFramePort;
  where: MarketingFramePort;
  uses: MarketingFramePort;
  share: MarketingFramePort;
  blocks: MarketingFramePort;
  offers: MarketingOfferPort;
}

export class MarketingExperience {
  private readonly timeline: LinearMarketingTimeline;
  private readonly presentation: MarketingPresentationPort;
  private readonly world: MarketingFramePort;
  private readonly navigation: MarketingFramePort;
  private readonly editorial: MarketingFramePort;
  private readonly where: MarketingFramePort;
  private readonly uses: MarketingFramePort;
  private readonly share: MarketingFramePort;
  private readonly blocks: MarketingFramePort;
  private readonly offers: MarketingOfferPort;
  private destroyed = false;
  private layout: MarketingLayout | null = null;
  private lastProofs: ProductProofsSnapshot | null = null;
  private routeWasHidden = false;
  private readonly narrative: NarrativeDirector;

  constructor(private readonly root: HTMLElement, options: MarketingExperienceOptions) {
    this.narrative = new NarrativeDirector(root);
    this.timeline = options.timeline;
    this.presentation = options.presentation;
    this.world = options.world;
    this.navigation = options.navigation;
    this.editorial = options.editorial;
    this.where = options.where;
    this.uses = options.uses;
    this.share = options.share;
    this.blocks = options.blocks;
    this.offers = options.offers;
  }

  renderAt(input: MarketingRenderInput): ExperienceSnapshot {
    if (this.destroyed) throw new Error("MarketingExperience has been destroyed.");
    const rawFrame = this.timeline.sample(input.scrollY, input.viewport, input.motion);
    const cinema=this.root.ownerDocument?.defaultView?.__BB_CINEMA__;
    const frame = cinema?.frame(rawFrame)??rawFrame;
    const world = this.world.render(frame, input.now);
    this.navigation.render(frame, input.now);
    const presentation = this.presentation.render(frame);
    /*
     * Пока маршрут не начался, его блоки не видны — а считали они себя каждый
     * кадр. Профиль первого экрана: clipFrom, rounded, fitArtboards и
     * applyDesktopArtboardLayout забирали 150–250 мс каждую секунду, то есть
     * около пятой части главного потока, ничего при этом не показывая.
     * Один кадр после ухода маршрута из вида всё же считается: он оставляет
     * блоки в согласованном состоянии, а не замороженными на полпути.
     */
    if (frame.handoff <= 0.001 && this.lastProofs && this.routeWasHidden) {
      return { frame, world, presentation, proofs: this.lastProofs, offers: this.offers.snapshot() };
    }
    this.routeWasHidden = frame.handoff <= 0.001;
    const where = this.where.render(frame, input.now);
    /*
     * Блоки ниже читают не положение секции в документе, а ход собственной
     * анимации: она начинается, когда кадр встал на место, и заканчивается,
     * пока он ещё стоит. Без этого начало и конец движения проходили мимо.
     */
    this.blocks.render(frame, input.now);
    const pinned = cinema?.active ? cinema.frame(withPinnedProgress(frame,this.layout)) : withPinnedProgress(frame, this.layout);
    this.narrative.render(pinned, input.now);
    this.editorial.render(pinned, input.now);
    const uses = this.uses.render(pinned, input.now);
    const share = this.share.render(pinned, input.now);
    const proofs = { state: "managed" as const, where, uses, share };
    this.lastProofs = proofs;
    const offers = this.offers.snapshot();
    return { frame, world, presentation, proofs, offers };
  }

  resize(viewport?: MarketingViewport): MarketingLayout | null {
    if (this.destroyed) return null;
    const resolvedViewport = viewport ?? (
      (this.root.ownerDocument.defaultView?.innerWidth ?? 1) < 700 ? "mobile" : "desktop"
    );
    const layout = this.presentation.measure();
    this.layout = layout;
    this.timeline.measure(layout);
    this.world.resize?.(resolvedViewport, layout);
    this.navigation.resize?.(resolvedViewport, layout);
    this.editorial.resize?.(resolvedViewport, layout);
    this.where.resize?.(resolvedViewport, layout);
    this.uses.resize?.(resolvedViewport, layout);
    this.share.resize?.(resolvedViewport, layout);
    this.blocks.resize?.(resolvedViewport, layout);
    return layout;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.narrative.destroy();
    this.blocks.destroy?.();
    this.share.destroy?.();
    this.uses.destroy?.();
    this.where.destroy?.();
    this.editorial.destroy?.();
    this.navigation.destroy?.();
    this.world.destroy?.();
    this.offers.destroy?.();
    this.presentation.destroy();
  }

  get route(): HTMLElement { return this.root; }
}
